import { useMemo } from "react";
import { useStore } from "ra-core";

import type {
  DealStage,
  IdealCustomer,
  LabeledValue,
  NoteStatus,
} from "../types";
import type { NightPlan } from "../../../../supabase/functions/_shared/leadAgent.ts";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
  /** iTHNK: ideal customer profiles for the fit score */
  ideal_customers?: IdealCustomer[];
  /** iTHNK: lead engine look-ups per engine per day (default 50) */
  lead_engine_daily_cap?: number;
  /** iTHNK: the website the lead agent reads to learn what we sell (default ithnk.com) */
  our_website?: string;
  /** iTHNK: brochures the lead agent may offer in a first email */
  lead_brochures?: { name: string; url: string; about?: string }[];
  /** iTHNK: the Entra app (client) ID the CRM signs in to Outlook with */
  outlook_client_id?: string;
  /** iTHNK: lead emails show as from this address and are BCC'd to it (default sales@ithnk.com) */
  outlook_send_as?: string;
  /** iTHNK: who lead emails show as from by default: the sales address or the signed-in user personally */
  outlook_default_from?: "sales" | "personal";
  /** iTHNK: signatures Personalise puts under a lead email, per sender ({name} is the signed-in user) */
  signature_personal?: string;
  signature_sales?: string;
  /** iTHNK outreach: most emails sent to one pre-lead, first included (1-3, default 3) */
  outreach_max_touches?: number;
  /** iTHNK outreach: days after the previous email for each follow-up (default [4, 7]) */
  outreach_followup_days?: number[];
  /** iTHNK outreach: weekdays emails may go out, Sunday = 0 (default Sun-Thu) */
  outreach_send_days?: number[];
  /** iTHNK outreach: daily send window "HH:MM-HH:MM" in the timezone below (default 09:00-16:00) */
  outreach_send_hours?: string;
  /** iTHNK outreach: IANA timezone of the send window (default Africa/Cairo) */
  outreach_timezone?: string;
  /** iTHNK outreach: most emails sent per day (default 30) */
  outreach_daily_cap?: number;
  /** iTHNK outreach: closing line added to every email so a reply can opt out */
  outreach_opt_out_line?: string;
  /** iTHNK outreach: https link where a prospect books a call; the suggested reply to an interested one offers it */
  booking_url?: string;
  /** iTHNK: the nightly lead run finds pre-leads from 00:05 UTC (default on) */
  lead_night_find?: boolean;
  /** iTHNK: the nightly lead run sends the first emails itself, within the daily cap (default off: drafts wait for review) */
  lead_night_send?: boolean;
  /** iTHNK: the brief the nightly run searches with: the segments last searched on the lead agent */
  lead_night_plan?: NightPlan;
  /** iTHNK: leads marked "Not a fit" with the reason; the lead agent avoids their like (latest 60) */
  lead_rejections?: {
    name: string;
    domain?: string;
    industry?: string;
    segment?: string;
    reason?: string;
  }[];
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => ({ ...defaultConfiguration, ...config }), [config]);
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
